import { createApi } from '@reduxjs/toolkit/query/react';

import { AGORA_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { mapRequest, mapResolvedRequest } from 'services/requestHelpers';
import { mapQueueItem } from 'services/requestQueueHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = {
    RequestQueue: 'RequestQueue',
    Request: 'Request',
};

// Grid columns own the sort allowlist (enableSorting); map direction to wire only.
const resolvedSortParam = (sort) => `resolved_at_utc:${sort?.[0]?.desc === false ? 'asc' : 'desc'}`;

export const agoraMonitorApi = createApi({
    baseQuery: getBaseQuery(AGORA_API_URL),
    reducerPath: 'agoraMonitorApi',
    tagTypes: Object.values(TagType),
    refetchOnMountOrArgChange: true,
    endpoints: (build) => ({
        getPagedReferrals: build.query({
            query: ({ page, search, timeFrameFilter, filterBody }) => ({
                url: '/referrals/paged',
                method: 'POST',
                params: {
                    page,
                    search_text: search ?? '',
                    date_filter: timeFrameFilter,
                },
                body: filterBody,
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving referrals.'),
        }),
        getReferralCount: build.query({
            query: ({ search, timeFrameFilter, filterBody }) => ({
                url: '/referrals/count',
                method: 'POST',
                params: {
                    search_text: search ?? '',
                    date_filter: timeFrameFilter,
                },
                body: {
                    additional_filters: filterBody.additional_filters,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the referral count.',
            ),
        }),
        postSearchTreatments: build.query({
            query: ({
                memberId,
                patientFirstName,
                patientLastName,
                patientDob,
                treatmentId,
                referralId,
                authId,
            }) => ({
                url: 'treatments',
                method: 'POST',
                body: {
                    member_id: memberId,
                    patient_first_name: patientFirstName,
                    patient_last_name: patientLastName,
                    patient_dob: patientDob,
                    treatment_id: treatmentId,
                    friendly_id: referralId,
                    case_id: authId,
                },
            }),
        }),
        getRequests: build.query({
            // Open queue stays fetch-all + client filter; API max 500 matches planned open volume.
            query: () => ({ url: '/requests', params: { limit: 500 } }),
            transformResponse: ({ requests }) => requests.map(mapRequest),
            providesTags: [TagType.Request],
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
        }),
        getResolvedRequests: build.query({
            // Scalar filter args so undefined vs {} from FilterPane share one cache key.
            query: ({ page, sort, assignedTo, requestTypeCode, requestCategoryKey }) => ({
                url: '/requests/resolved',
                params: {
                    page,
                    page_size: 50,
                    sort: resolvedSortParam(sort),
                    assigned_to: assignedTo,
                    request_type_code: requestTypeCode,
                    request_category_key: requestCategoryKey,
                },
            }),
            transformResponse: ({ requests, total }) => ({
                requests: requests.map(mapResolvedRequest),
                total,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving resolved requests.',
            ),
        }),
        getResolvedRequestFilterOptions: build.query({
            // Categories/types change rarely; avoid remount refetch thrash.
            query: () => '/requests/resolved/filter-options',
            keepUnusedDataFor: 300,
            transformResponse: (response) => ({
                requestTypes: response.request_types ?? [],
                requestCategories: response.request_categories ?? [],
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving resolved request filters.',
            ),
        }),
        getRequestQueues: build.query({
            query: () => '/request-queues',
            transformResponse: ({ queues }) => (queues ?? []).map(mapQueueItem),
            providesTags: [TagType.RequestQueue],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving request queues.',
            ),
        }),
        createRequestQueue: build.mutation({
            query: ({ name, visibility, criteria, sort }) => ({
                url: '/request-queues',
                method: 'POST',
                body: {
                    name,
                    visibility,
                    criteria: criteria ?? {},
                    sort: sort ?? [],
                },
            }),
            transformResponse: mapQueueItem,
            invalidatesTags: (_, error) => (!error ? [TagType.RequestQueue] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred creating the request queue.',
            ),
        }),
        updateRequestQueue: build.mutation({
            query: ({ queueId, name, criteria, sort }) => ({
                url: `/request-queues/${queueId}`,
                method: 'PATCH',
                body: {
                    name,
                    criteria: criteria ?? {},
                    sort: sort ?? [],
                },
            }),
            transformResponse: mapQueueItem,
            invalidatesTags: (_, error) => (!error ? [TagType.RequestQueue] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating the request queue.',
            ),
        }),
        getRequestForConversation: build.query({
            query: (conversationId) => `/requests/by-conversation/${conversationId}`,
            transformResponse: (response) => ({ requestId: response.request_id }),
            transformErrorResponse: (error) =>
                error.status === 404
                    ? error
                    : handleRTKQueryError('An error occurred finding the request.')(error),
        }),
        snoozeRequest: build.mutation({
            query: ({ requestId, availableAtUtc, label }) => ({
                url: `/requests/${requestId}/snooze`,
                method: 'POST',
                body: {
                    available_at_utc: availableAtUtc,
                    label: label || null,
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagType.Request] : undefined),
            transformErrorResponse: handleRTKQueryError('An error occurred snoozing the request.'),
        }),
        deleteRequestQueue: build.mutation({
            query: (queueId) => ({
                url: `/request-queues/${queueId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) => (!error ? [TagType.RequestQueue] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred deleting the request queue.',
            ),
        }),
    }),
});

export const {
    useGetPagedReferralsQuery,
    useGetReferralCountQuery,
    useGetRequestsQuery,
    useGetResolvedRequestsQuery,
    useGetResolvedRequestFilterOptionsQuery,
    useGetRequestQueuesQuery,
    useCreateRequestQueueMutation,
    useUpdateRequestQueueMutation,
    useDeleteRequestQueueMutation,
    useGetRequestForConversationQuery,
    useSnoozeRequestMutation,
    usePostSearchTreatmentsQuery,
} = agoraMonitorApi;
